import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import Title from "antd/es/typography/Title";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../api";
import Loader from "../../../../components/Loader";
import { IntTplInsurancePeriod } from "../../../../domain/types";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";
const Text = Typography;

const InternationalTplInsuranceDays = () => {
  const { t } = useTranslation();
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [editedDay, setEditedDay] =
    useState<Partial<IntTplInsurancePeriod> | null>(null);

  const onCloseDrawer = () => {
    setDrawerStatus(false);
  };

  const setEditedField = (field: keyof IntTplInsurancePeriod, value: any) => {
    setEditedDay({
      productId: 1,
      ...editedDay,
      [field]: value,
    });
  };

  const {
    data: insuranceDaysList,
    refetch: refetchIntDays,
    isLoading: isInsuranceDaysLoading,
  } = api.useGetIntTplDaysQuery(undefined);

  const { data: insuranceDayById, isLoading: isInsuranceDayByIdLoading } =
    api.useGetIntTplDayByIdQuery(editedDay?.id as number, {
      skip: !editedDay?.id,
    });

  const [createDay, { isLoading: isCreateDayLoading }] =
    api.useCreateIntTplDayMutation();

  const [updateDay, { isLoading: isUpdateDayLoading }] =
    api.useUpdateIntTplDayMutation();

  const [
    removeDay,
    { isLoading: isRemoveDayLoading, isSuccess: removeDaySuccess },
  ] = api.useDeleteIntTplDayMutation();

  const _createDay = async () => {
    if (editedDay) {
      try {
        await createDay(editedDay).unwrap();
        refetchIntDays();
        toast.success(`${t("numberOfDays")} ${t("addedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  const _updateDay = async () => {
    if (editedDay) {
      try {
        await updateDay(editedDay).unwrap();
        refetchIntDays();
        toast.success(`${t("numberOfDays")} ${t("updatedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  useEffect(() => {
    if (removeDaySuccess) {
      refetchIntDays();
    }
  }, [removeDaySuccess, refetchIntDays]);

  useEffect(() => {
    if (insuranceDayById) {
      setEditedDay(insuranceDayById);
      setDrawerStatus(true);
    }

    return () => {};
  }, [insuranceDayById]);

  const MainLoader = isInsuranceDaysLoading;

  const DrawerLoader =
    isUpdateDayLoading ||
    isRemoveDayLoading ||
    isInsuranceDayByIdLoading ||
    isCreateDayLoading;

  return (
    <>
      {MainLoader ? (
        <Loader />
      ) : (
        <>
          <Flex justify="space-between">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => [setEditedField("id", 0), setDrawerStatus(true)]}
            >
              {t("create")}
            </Button>

            <AdminLangSwicher />
          </Flex>

          <Divider />

          <Flex gap={20} wrap="wrap" justify="space-evenly">
            {insuranceDaysList?.map((item, index) => (
              <Badge.Ribbon
                key={index}
                style={{
                  marginTop: "-20px",
                }}
                text={item.status === "active" ? t("active") : t("inActive")}
                color={item.status === "active" ? "green" : "red"}
              >
                <Card
                  hoverable
                  style={{
                    maxWidth: "250px",
                    width: "100vw",
                    display: "grid",
                    placeItems: "center",
                    height: "100%",
                  }}
                  onClick={() => [
                    setEditedField("id", item.id),
                    setDrawerStatus(!drawerStatus),
                  ]}
                >
                  <Title level={5}>{item.days || t("textNotFound")}</Title>
                </Card>
              </Badge.Ribbon>
            ))}
          </Flex>

          <Drawer
            title={editedDay?.id === 0 ? t("create") : t("update")}
            onClose={onCloseDrawer}
            open={drawerStatus}
          >
            {DrawerLoader ? (
              <Loader />
            ) : (
              <Flex vertical gap={20}>
                <Space direction="vertical">
                  <Text>{t("numberOfDays")}</Text>
                  <Input
                    size="large"
                    placeholder={t("numberOfDays")}
                    type="number"
                    value={editedDay?.days || ""}
                    onChange={(event) =>
                      setEditedField("days", event?.target?.value)
                    }
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("status")}</Text>
                  <Select
                    size="large"
                    showSearch
                    style={{ width: "100%", display: "block" }}
                    placeholder={t("status")}
                    optionFilterProp="label"
                    allowClear
                    value={editedDay?.status || undefined}
                    onChange={(value) => setEditedField("status", value)}
                    options={[
                      {
                        status: "active",
                        name: t("active"),
                      },
                      {
                        status: "inactive",
                        name: t("inActive"),
                      },
                    ]?.map((item) => ({
                      value: item.status,
                      label: item.name,
                    }))}
                  />
                </Space>

                <>
                  <Divider style={{ margin: 0 }} />
                  {editedDay?.id === 0 ? (
                    <Button
                      disabled={editedDay && editedDay.status ? false : true}
                      size="large"
                      type="primary"
                      onClick={_createDay}
                    >
                      {t("create")}
                    </Button>
                  ) : null}

                  {editedDay && editedDay.id && editedDay.id !== 0 ? (
                    <>
                      <Button size="large" type="primary" onClick={_updateDay}>
                        {t("update")}
                      </Button>

                      <Popconfirm
                        title={t("certainToDeleteIt")}
                        onConfirm={() => [
                          removeDay(editedDay?.id as number),
                          onCloseDrawer(),
                        ]}
                        onCancel={() => {}}
                        okText="Yes"
                        cancelText="No"
                        placement="bottom"
                      >
                        <Button
                          danger
                          size="large"
                          style={{
                            width: "100%",
                          }}
                        >
                          {t("delete")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              </Flex>
            )}
          </Drawer>
        </>
      )}
    </>
  );
};

export default InternationalTplInsuranceDays;
